import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MapPin, User, Building2, Calendar as CalIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { SECTORS, NEEDS, STATUSES, toDatetimeLocal, fromDatetimeLocal } from "@/lib/constants";
import { toast } from "sonner";

const initialForm = {
  phone: "",
  brand_name: "",
  contact_name: "",
  sector: "guzellik",
  sector_other: "",
  needs: [],
  price_info: "",
  location: "",
  created_at: toDatetimeLocal(new Date().toISOString()),
  current_status: "islemde",
};

const Label = ({ children }) => (
  <label className="block text-xs font-semibold tracking-wider uppercase text-slate-400 mb-2">{children}</label>
);

const inputCls =
  "w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder:text-slate-400 transition-colors";

const inputWithIcon =
  "w-full h-11 rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder:text-slate-400 transition-colors";

export default function CallNew() {
  const nav = useNavigate();
  const [f, setF] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggleNeed = (v) => {
    setF((prev) => ({
      ...prev,
      needs: prev.needs.includes(v) ? prev.needs.filter((x) => x !== v) : [...prev.needs, v],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.phone || !f.brand_name || !f.contact_name) {
      setError("Lütfen zorunlu alanları doldurun.");
      return;
    }
    if (f.sector === "diger" && !f.sector_other.trim()) {
      setError("Diğer sektör için lütfen açıklama girin.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...f,
        created_at: fromDatetimeLocal(f.created_at) || new Date().toISOString(),
      };
      const { data } = await api.post("/calls", payload);
      toast.success("Kayıt oluşturuldu");
      nav(`/calls/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mb-10 border-b border-slate-100 pb-6" data-testid="new-header">
        <h1 className="text-4xl tracking-tight font-bold text-slate-900">Yeni Çağrı Ekle</h1>
        <p className="text-sm text-slate-500 mt-2">Yeni bir satış çağrısını sisteme kaydedin.</p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-10" data-testid="new-form">
        <div className="grid grid-cols-2 gap-x-12 gap-y-8">
          <div>
            <Label>Telefon Numarası *</Label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="tel"
                required
                value={f.phone}
                onChange={(e) => setF({ ...f, phone: e.target.value })}
                placeholder="5XX XXX XX XX"
                data-testid="field-phone"
                className={inputWithIcon}
              />
            </div>
          </div>

          <div>
            <Label>Marka Adı *</Label>
            <div className="relative">
              <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                required
                value={f.brand_name}
                onChange={(e) => setF({ ...f, brand_name: e.target.value })}
                placeholder="Örn. Glow Beauty"
                data-testid="field-brand"
                className={inputWithIcon}
              />
            </div>
          </div>

          <div>
            <Label>Yetkili Adı *</Label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                required
                value={f.contact_name}
                onChange={(e) => setF({ ...f, contact_name: e.target.value })}
                placeholder="Örn. Ayşe Yılmaz"
                data-testid="field-contact"
                className={inputWithIcon}
              />
            </div>
          </div>

          <div>
            <Label>Sektör</Label>
            <select
              value={f.sector}
              onChange={(e) => setF({ ...f, sector: e.target.value })}
              data-testid="field-sector"
              className={inputCls}
            >
              {SECTORS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {f.sector === "diger" && (
              <input
                type="text"
                value={f.sector_other}
                onChange={(e) => setF({ ...f, sector_other: e.target.value })}
                placeholder="Sektörünüzü yazın"
                data-testid="field-sector-other"
                className={`${inputCls} mt-3`}
              />
            )}
          </div>

          <div className="col-span-2">
            <Label>İhtiyaç Türü (birden fazla seçilebilir)</Label>
            <div className="flex flex-wrap gap-2" data-testid="needs-chips">
              {NEEDS.map((n) => {
                const active = f.needs.includes(n.value);
                return (
                  <button
                    key={n.value}
                    type="button"
                    onClick={() => toggleNeed(n.value)}
                    data-testid={`need-chip-${n.value}`}
                    className={`inline-flex items-center justify-center px-4 py-2 rounded-full border text-sm font-medium transition-colors duration-150 ${
                      active
                        ? "bg-orange-50 border-orange-200 text-orange-600"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {n.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="col-span-2">
            <Label>Fiyat Bilgisi</Label>
            <input
              type="text"
              value={f.price_info}
              onChange={(e) => setF({ ...f, price_info: e.target.value })}
              placeholder="Örn. Aylık 20.000 TL bütçe ayırabilir."
              data-testid="field-price"
              className={inputCls}
            />
          </div>

          <div>
            <Label>Lokasyon</Label>
            <div className="relative">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={f.location}
                onChange={(e) => setF({ ...f, location: e.target.value })}
                placeholder="İstanbul, Türkiye"
                data-testid="field-location"
                className={inputWithIcon}
              />
            </div>
          </div>

          <div>
            <Label>Tarih & Saat</Label>
            <div className="relative">
              <CalIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="datetime-local"
                value={f.created_at}
                onChange={(e) => setF({ ...f, created_at: e.target.value })}
                data-testid="field-datetime"
                className={inputWithIcon}
              />
            </div>
          </div>

          <div className="col-span-2">
            <Label>Başlangıç Durumu</Label>
            <div className="flex flex-wrap gap-2" data-testid="status-chips">
              {STATUSES.map((s) => {
                const active = f.current_status === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setF({ ...f, current_status: s.value })}
                    data-testid={`status-chip-${s.value}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors duration-150 ${
                      active ? s.chip : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span className={`status-dot ${s.dot}`} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {error && (
          <div data-testid="new-error" className="mt-6 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-10 pt-6 border-t border-slate-100">
          <button
            type="button"
            onClick={() => nav("/calls")}
            data-testid="new-cancel"
            className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-6 py-2.5 font-medium text-sm transition-colors duration-150"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            data-testid="new-submit"
            className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-8 py-2.5 font-medium text-sm transition-colors duration-150 disabled:opacity-50"
          >
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
    </Layout>
  );
}
